import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot, Megaphone, DollarSign, Phone, Store, Package, ShoppingCart,
  ArrowRight, ArrowUpRight, Sparkles, Shield, Zap, Globe, Users,
  TrendingUp, CreditCard, Target, Network, Brain, Cpu, Truck,
  ChevronRight, Layers, BarChart3, Eye, Star, Check, MessageCircle
} from 'lucide-react';
import { CILogo } from '../components/CILogo';
import { LINKS } from '../config/siteLinks';
import PhoneStartModal from '../components/PhoneStartModal';
import ShopifyAppStoreCTA from '../components/ShopifyAppStoreCTA';
import QRCodeStart from '../components/QRCodeStart';

// ─── Section badge ─────────────────────────────────────────
function Badge({ children, color = '#3EF1C7' }) {
  return (
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6"
      style={{ borderColor: color + '33', backgroundColor: color + '0D' }}>
      <span className="text-sm font-medium" style={{ color }}>{children}</span>
    </div>
  );
}

// ─── Reusable card ─────────────────────────────────────────
function Card({ children, className = '' }) {
  return (
    <div className={`rounded-xl border border-[#22304A] bg-[#0D1628] p-6 ${className}`}>
      {children}
    </div>
  );
}

// ─── Interactive mock terminal ─────────────────────────────
function MockTerminal({ lines }) {
  return (
    <div className="rounded-lg border border-[#22304A] bg-[#070D1A] p-4 font-mono text-xs overflow-hidden">
      {lines.map((line, i) => (
        <div key={i} className={`py-0.5 ${line.startsWith('>') ? 'text-[#3EF1C7]' : 'text-[#94A3B8]'}`}>
          {line}
        </div>
      ))}
      <div className="text-[#3EF1C7] animate-pulse mt-1">▋</div>
    </div>
  );
}

export default function AgentOnboardingPage() {
  const [activeSegment, setActiveSegment] = useState('retail');
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneModalContext, setPhoneModalContext] = useState({});
  const openPhoneModal = (ctx = {}) => { setPhoneModalContext(ctx); setPhoneModalOpen(true); };
  const [showcaseProducts, setShowcaseProducts] = useState([
    { name: 'Classic Oxford Shirt', style: 'Button-Down', color: 'Navy', sizes: ['S', 'M', 'L', 'XL'], price: '$89', wholesale: '$44', moq: 24 },
    { name: 'Slim Fit Chinos', style: 'Tapered', color: 'Khaki', sizes: ['30', '32', '34', '36'], price: '$95', wholesale: '$47', moq: 36 },
    { name: 'Merino Crew Sweater', style: 'Regular Fit', color: 'Charcoal', sizes: ['S', 'M', 'L', 'XL', 'XXL'], price: '$120', wholesale: '$58', moq: 18 },
  ]);

  const segments = {
    retail: {
      icon: Store,
      title: 'Retail',
      features: [
        'In-store + online unified inventory',
        'POS integration with real-time CI Score',
        'Foot traffic analytics & conversion tracking',
        'Voice agent for phone orders & support',
        'Local marketing via geo-targeting agent',
      ],
    },
    wholesale: {
      icon: Package,
      title: 'Wholesale',
      features: [
        'Bulk pricing tiers with margin protection',
        'Buyer relationship management',
        'Minimum order quantities & volume discounts',
        'Showcase briefs for major retail buyers',
        'Funding agent for inventory financing',
      ],
    },
    ecommerce: {
      icon: ShoppingCart,
      title: 'E-commerce',
      features: [
        'Shopify / WooCommerce / BigCommerce integration',
        'Cart optimization & abandoned cart recovery',
        'Multi-carrier shipping rate optimization',
        'AI-powered product recommendations',
        'Cross-channel distribution via thedeals.ai',
      ],
    },
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-white" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ═══════════════════ NAV ═══════════════════ */}
      <nav className="fixed top-0 w-full z-50 bg-[#0B1120]/80 backdrop-blur-xl border-b border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <CILogo size={32} theme="dark" />
            <span className="font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Commerce Index</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Home</Link>
            <button onClick={() => openPhoneModal({ source: 'agent_onboarding_nav' })} className="px-4 py-2 rounded-lg text-sm font-medium bg-[#3EF1C7] text-[#0B1120] hover:bg-[#35D9B2] transition-colors">
              Start free
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-16">

        {/* ═══════════════════ 1. HERO ═══════════════════ */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#3EF1C7]/5 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-[#7A5CFF]/5 rounded-full blur-3xl" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center max-w-4xl mx-auto">
              <Badge>Agent Onboarding</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Your AI Team is Ready.{' '}
                <span className="text-[#3EF1C7]">Explore Before You Start.</span>
              </h1>
              <p className="text-lg sm:text-xl text-[#94A3B8] mb-10 max-w-2xl mx-auto">
                Marketing, Funding, Voice — agents that work for your Retail, Wholesale, and E-commerce business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button onClick={() => openPhoneModal({ source: 'agent_onboarding_hero' })} className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg text-base font-semibold bg-[#3EF1C7] text-[#0B1120] hover:bg-[#35D9B2] transition-colors">
                  <Phone className="w-4 h-4" /> Text us to start
                </button>
                <a href="#agents" className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg text-base font-semibold border border-[#22304A] text-white hover:bg-[#111A2E] transition-colors">
                  Explore Agents Below <ChevronRight className="w-4 h-4" />
                </a>
                <Link to="/dashboard/org-chart" className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg text-base font-semibold border border-[#7A5CFF]/30 text-[#7A5CFF] hover:bg-[#7A5CFF]/10 transition-colors">
                  See full org chart <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="flex flex-col md:flex-row gap-4 justify-center items-center mt-8">
                <QRCodeStart size={140} label="Or scan to start on WhatsApp" />
                <ShopifyAppStoreCTA variant="secondary" label="Already on Shopify? Install from App Store" subtext="Auto-detects your store. No URL entry." />
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ 2. MARKETING AGENT ═══════════════════ */}
        <section id="agents" className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge color="#FFD166">Marketing Agent</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Your CMO That Never Sleeps
                </h2>
                <p className="text-[#94A3B8] mb-8">
                  The CMO Agent orchestrates your entire marketing strategy — detecting revenue dips, launching recovery campaigns, and allocating budget across channels automatically.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    { icon: Target, label: 'Budget allocation across 5 channels' },
                    { icon: Megaphone, label: 'Cross-channel campaigns (Reddit, HackerNews, content, geo, distribution)' },
                    { icon: Brain, label: 'Revenue dip detection & auto-recovery' },
                    { icon: Users, label: 'Sub-agent delegation for specialized tasks' },
                    { icon: BarChart3, label: 'Three strategies: Acquisition, Awareness, Balanced' },
                  ].map(f => (
                    <div key={f.label} className="flex items-start gap-3">
                      <f.icon className="w-5 h-5 text-[#FFD166] mt-0.5 flex-shrink-0" />
                      <span className="text-[#9CB0C7]">{f.label}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => openPhoneModal({ source: 'agent_onboarding_marketing', intended_feature: 'marketing_agent' })} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold bg-[#FFD166] text-[#0B1120] hover:bg-[#FFBF33] transition-colors">
                  Try Marketing Agent <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <Card>
                <h4 className="text-sm font-semibold text-[#FFD166] mb-4">Sample Campaign Brief</h4>
                <MockTerminal lines={[
                  '> /cmo launch-campaign --type=acquisition',
                  '',
                  'Campaign: Q1 Product Launch',
                  'Budget: $5,000/mo',
                  '',
                  'Channel Allocation (Acquisition):',
                  '  Reddit Agent      — $1,250 (25%)',
                  '  Geo Targeting      — $1,250 (25%)',
                  '  Content Writer     — $1,000 (20%)',
                  '  Distribution       — $1,000 (20%)',
                  '  HackerNews Agent   — $500  (10%)',
                  '',
                  'Status: 5 sub-agents deployed',
                  'Expected reach: 120K impressions',
                ]} />
              </Card>
            </div>
          </div>
        </section>

        {/* ═══════════════════ 3. FUNDING AGENT ═══════════════════ */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <Card>
                  <h4 className="text-sm font-semibold text-[#3EF1C7] mb-4">Funding Qualification Flow</h4>
                  <MockTerminal lines={[
                    '> /funding qualify --revenue=250000 --months=18',
                    '',
                    'Analyzing credit profile...',
                    'CI Score: 782 | Margin Guard: 91/100',
                    'Demand Signals: Strong (social engagement +34%)',
                    '',
                    'Agent Negotiation Results:',
                    '  Cross River Bank  — Term Loan $75K @ 8.2% ✓',
                    '  Celtic Bank       — SBA 7(A) $150K @ 6.8% ✓',
                    '  Column Bank       — LOC $50K @ 7.1% ✓',
                    '  WebBank           — Rev-Based $60K @ 9.5%',
                    '',
                    'Best Offer: Celtic Bank SBA 7(A)',
                    'Rate reduction: -1.5% (CI intelligence data)',
                    'Decision: 48hrs | Funding: 7 days',
                  ]} />
                </Card>
              </div>
              <div className="order-1 lg:order-2">
                <Badge>Funding Agent</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  6 Agents Negotiate Your Best Rate
                </h2>
                <p className="text-[#94A3B8] mb-6">
                  The Orchestrator Agent coordinates BankAgent, PrivateCreditAgent, PublicCreditAgent, BondAgent, and HELOCAgent to find you the best financing — using your CI Score as alternative data for better rates.
                </p>

                <h4 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider mb-3">Banking Partners</h4>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { name: 'Cross River Bank', detail: '4hr decision' },
                    { name: 'Celtic Bank', detail: 'SBA specialist' },
                    { name: 'WebBank', detail: 'Marketplace lending' },
                    { name: 'Column Bank', detail: 'Developer-first' },
                  ].map(b => (
                    <div key={b.name} className="px-3 py-2 rounded-lg border border-[#22304A] bg-[#070D1A]">
                      <div className="text-sm font-medium text-white">{b.name}</div>
                      <div className="text-xs text-[#64748B]">{b.detail}</div>
                    </div>
                  ))}
                </div>

                <h4 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider mb-3">Loan Products</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Term Loan', 'Line of Credit', 'Revenue-Based', 'Inventory Finance', 'MCA', 'SBA 7(A)', 'SBA Express', 'Demand-Accelerated'].map(p => (
                    <span key={p} className="px-3 py-1 text-xs rounded-full border border-[#22304A] text-[#94A3B8]">{p}</span>
                  ))}
                </div>

                <h4 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider mb-3">BaaS Platforms</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Treasury Prime', 'Unit', 'Synctera', 'Bond'].map(p => (
                    <span key={p} className="px-3 py-1 text-xs rounded-full border border-[#7A5CFF]/30 text-[#7A5CFF]">{p}</span>
                  ))}
                </div>

                <div className="flex items-center gap-3 p-3 rounded-lg border border-[#3EF1C7]/20 bg-[#3EF1C7]/5 mb-8">
                  <CreditCard className="w-5 h-5 text-[#3EF1C7] flex-shrink-0" />
                  <span className="text-sm text-[#9CB0C7]">
                    <strong className="text-white">Ramp integration</strong> — Corporate card & expense management for merchants
                  </span>
                </div>

                <button onClick={() => openPhoneModal({ source: 'agent_onboarding_funding', intended_feature: 'funding_engine' })} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold bg-[#3EF1C7] text-[#0B1120] hover:bg-[#35D9B2] transition-colors">
                  Try Funding Engine <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ 4. VOICE AGENT ═══════════════════ */}
        <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge color="#7A5CFF">Voice Agent</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Build Trust Through Voice
                </h2>
                <p className="text-[#94A3B8] mb-8">
                  Pella voice agent integration brings human-level phone interactions to your commerce operations — from customer support to supplier negotiations.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    { icon: Phone, label: 'Inbound & outbound call handling' },
                    { icon: Shield, label: 'Customer support with full order context' },
                    { icon: DollarSign, label: 'Supplier negotiation & price matching' },
                    { icon: Check, label: 'Order confirmations & delivery updates' },
                  ].map(f => (
                    <div key={f.label} className="flex items-start gap-3">
                      <f.icon className="w-5 h-5 text-[#7A5CFF] mt-0.5 flex-shrink-0" />
                      <span className="text-[#9CB0C7]">{f.label}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => openPhoneModal({ source: 'agent_onboarding_voice', intended_feature: 'voice_agent' })} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold border border-[#7A5CFF] text-[#7A5CFF] hover:bg-[#7A5CFF]/10 transition-colors">
                  Try Voice Agent <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {[
                  { segment: 'Retail', desc: 'Phone orders, product inquiries, store hours, loyalty program support', icon: Store, color: '#3EF1C7' },
                  { segment: 'Wholesale', desc: 'Buyer negotiations, volume discount discussions, delivery scheduling', icon: Package, color: '#FFD166' },
                  { segment: 'E-commerce', desc: 'WISMO calls, return processing, product recommendations, cart recovery', icon: ShoppingCart, color: '#7A5CFF' },
                ].map(s => (
                  <Card key={s.segment}>
                    <div className="flex items-center gap-3 mb-2">
                      <s.icon className="w-5 h-5" style={{ color: s.color }} />
                      <h4 className="font-semibold">{s.segment}</h4>
                    </div>
                    <p className="text-sm text-[#94A3B8]">{s.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ 5. BUSINESS SEGMENTS ═══════════════════ */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge>Business Segments</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Agents That Adapt to Your Business
              </h2>
              <p className="text-[#94A3B8] max-w-2xl mx-auto">
                Whether you run a storefront, supply wholesale, or sell online — every agent adjusts to your segment.
              </p>
            </div>

            <div className="flex justify-center gap-2 mb-10">
              {Object.entries(segments).map(([key, seg]) => (
                <button key={key} onClick={() => setActiveSegment(key)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeSegment === key
                      ? 'bg-[#3EF1C7] text-[#0B1120]'
                      : 'border border-[#22304A] text-[#94A3B8] hover:text-white hover:border-[#3EF1C7]/30'
                  }`}>
                  <seg.icon className="w-4 h-4" />
                  {seg.title}
                </button>
              ))}
            </div>

            <Card className="max-w-2xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                {React.createElement(segments[activeSegment].icon, { className: 'w-6 h-6 text-[#3EF1C7]' })}
                <h3 className="text-xl font-bold">{segments[activeSegment].title}</h3>
              </div>
              <div className="space-y-3">
                {segments[activeSegment].features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#3EF1C7] mt-1 flex-shrink-0" />
                    <span className="text-[#9CB0C7]">{f}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* ═══════════════════ 6. SHOWCASE / BUYER BRIEF BUILDER ═══════════════════ */}
        <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge color="#FFD166">Showcase Builder</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Build Buyer Briefs That Close Deals
              </h2>
              <p className="text-[#94A3B8] max-w-2xl mx-auto">
                Generate professional showcase briefs with your product catalog — SKU, style, color, and size matrix — ready for buyers at Nordstrom, Macy's, Target, and more.
              </p>
            </div>

            <Card className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h4 className="font-semibold text-[#FFD166]">Sample Showcase Brief</h4>
                <span className="text-xs px-3 py-1 rounded-full bg-[#3EF1C7]/10 text-[#3EF1C7] border border-[#3EF1C7]/20">Interactive Demo</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#22304A]">
                      <th className="text-left py-3 px-3 text-[#64748B] font-medium">Product</th>
                      <th className="text-left py-3 px-3 text-[#64748B] font-medium">Style</th>
                      <th className="text-left py-3 px-3 text-[#64748B] font-medium">Color</th>
                      <th className="text-left py-3 px-3 text-[#64748B] font-medium">Sizes</th>
                      <th className="text-right py-3 px-3 text-[#64748B] font-medium">Retail</th>
                      <th className="text-right py-3 px-3 text-[#64748B] font-medium">Wholesale</th>
                      <th className="text-right py-3 px-3 text-[#64748B] font-medium">MOQ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showcaseProducts.map((p, i) => (
                      <tr key={i} className="border-b border-[#22304A]/50 hover:bg-[#111A2E]/50">
                        <td className="py-3 px-3 font-medium text-white">{p.name}</td>
                        <td className="py-3 px-3 text-[#94A3B8]">{p.style}</td>
                        <td className="py-3 px-3 text-[#94A3B8]">{p.color}</td>
                        <td className="py-3 px-3">
                          <div className="flex gap-1">
                            {p.sizes.map(s => (
                              <span key={s} className="px-2 py-0.5 text-xs rounded bg-[#0B1120] border border-[#22304A] text-[#94A3B8]">{s}</span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right text-white">{p.price}</td>
                        <td className="py-3 px-3 text-right text-[#3EF1C7]">{p.wholesale}</td>
                        <td className="py-3 px-3 text-right text-[#94A3B8]">{p.moq}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 pt-4 border-t border-[#22304A] flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex gap-6 text-sm">
                  <span className="text-[#64748B]">Target Buyers: <span className="text-white">Nordstrom, Macy's, Target</span></span>
                  <span className="text-[#64748B]">Categories: <span className="text-white">Apparel</span></span>
                </div>
                <div className="flex gap-3">
                  <Link to="/channels" className="text-sm text-[#94A3B8] hover:text-white transition-colors">
                    View ChannelsHub →
                  </Link>
                  <button onClick={() => openPhoneModal({ source: 'agent_onboarding_showcase', intended_feature: 'showcase' })} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#FFD166] text-[#0B1120] hover:bg-[#FFBF33] transition-colors">
                    Try Showcase Builder <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* ═══════════════════ 7. HOW IT ALL WORKS ═══════════════════ */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <Badge>How It Works</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                From Sign-Up to Revenue Growth
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 max-w-6xl mx-auto">
              {[
                { icon: Sparkles, label: 'Sign Up', color: '#3EF1C7' },
                { icon: Bot, label: 'Agents Activate', color: '#3EF1C7' },
                { icon: Megaphone, label: 'Marketing Drives Traffic', color: '#FFD166' },
                { icon: DollarSign, label: 'Funding Fuels Growth', color: '#3EF1C7' },
                { icon: Phone, label: 'Voice Builds Trust', color: '#7A5CFF' },
                { icon: Eye, label: 'Showcases Reach Buyers', color: '#FFD166' },
                { icon: TrendingUp, label: 'Revenue Grows', color: '#3EF1C7' },
              ].map((step, i) => (
                <div key={i} className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-xl border border-[#22304A] bg-[#0D1628] flex items-center justify-center mb-3">
                    <step.icon className="w-6 h-6" style={{ color: step.color }} />
                  </div>
                  <span className="text-xs text-[#94A3B8]">{step.label}</span>
                  {i < 6 && <ChevronRight className="w-4 h-4 text-[#22304A] mt-2 hidden lg:block rotate-0" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ 8. GET STARTED CTA ═══════════════════ */}
        <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Start Your Agent-Powered Commerce Journey
            </h2>
            <p className="text-[#94A3B8] mb-10 max-w-xl mx-auto">
              One AI CEO + 13 directors + 60+ specialists. Multi-lender funding. Voice support. Showcase publishing. All from one platform.
            </p>
            <div className="flex flex-col md:flex-row gap-6 justify-center items-center mb-6">
              <button onClick={() => openPhoneModal({ source: 'agent_onboarding_final_cta' })} className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg text-base font-semibold bg-[#3EF1C7] text-[#0B1120] hover:bg-[#35D9B2] transition-colors">
                <Phone className="w-4 h-4" /> Text us to start
              </button>
              <span className="text-[#64748B] text-sm">or</span>
              <QRCodeStart size={140} label="Scan to start on WhatsApp" />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <ShopifyAppStoreCTA variant="secondary" label="Already on Shopify? Install from App Store" subtext="" />
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-semibold border border-[#22304A] text-white hover:bg-[#111A2E] transition-colors">
                Talk to Sales
              </Link>
              <a href="https://commerceindex.ai" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-semibold border border-[#7A5CFF]/30 text-[#7A5CFF] hover:bg-[#7A5CFF]/10 transition-colors">
                Visit commerceindex.ai <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* ═══════════════════ FOOTER ═══════════════════ */}
        <footer className="py-12 border-t border-[#22304A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CILogo size={24} variant="ring" theme="dark" />
              <span className="text-sm text-[#64748B]">© 2026 Commerce Index. All rights reserved.</span>
            </div>
            <div className="flex gap-6 text-sm text-[#64748B]">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/docs" className="hover:text-white transition-colors">Docs</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
        </footer>

      </div>

      <PhoneStartModal
        open={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        intendedPlan={phoneModalContext.intended_plan}
        intendedFeature={phoneModalContext.intended_feature}
      />
    </div>
  );
}
